import React from 'react';
import * as mobx from 'mobx';
import { isAfter, isBefore, isEqual, startOfDay } from 'date-fns';
import { FormattedMessage } from 'react-intl';
import * as IoC from 'typescript-ioc';
import { calcByteCodeSize } from '../../../../infrastructure/utils/common.utils';
import { ClientDepartmentValues, EventAccess, EventStatus, ICreateEventDTO, IDeliveryItemDetailsDTO, IEventAttributeValue, IEventCoordinatorDTO, IEventDetailsDTO, IEventsDAO } from '../../../../api/events.dao';
import { utcToZonedTime, zonedTimeToUtc } from '../../../../infrastructure/utils/date-fns-overrides.utils';
import { LONG_PLAIN_TEXT_LENGTH, LONG_RICH_TEXT_LENGTH, SHORT_PLAIN_TEXT_LENGTH, MINIMAL_SEARCH_LENGTH, TOOLTIP_GROUP_USERS_COUNT } from '../../../../constants/common.constants';
import { FooterLink } from '../../_manage-event/_edit-website/website-form-body/website-footer-links/website-footer-link.presentation';
import { getShortTextLengthError, getUrlError } from '../../../../infrastructure/utils/form-validation.utils';
import { LONG_PLAIN_TEXT_TOO_LONG_ERROR_TEXT, SHORT_PLAIN_TEXT_TOO_LONG_ERROR_TEXT } from '../../_manage-event/_edit-website/website-form-body/scheduled-emails-form/scheduled-email-form/scheduled-email-form.texts';
import { IUsersDAO } from '../../../../api/users.dao';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { IUserGroupDTO, IUserGroupsDAO } from '../../../../api/user-groups.dao';
import { stringifyStaffMembers } from '../../../../infrastructure/utils/users.utils';
import { IEventLinkDTO } from '../../../../api/event-links.dao';
import { CoordinatorItemsMapper } from '../../../../modules/coordinators/coordinator-items.mapper';
import { DeliveryTeamMemberItemsMapper } from '../../../../modules/delivery-team-members/delivery-teammember-items.mapper';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';
import { EventFormBlueprintsVM } from './event-form-blueprints.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { GlobalViewModel } from '../../../global.viewmodel';
import { convertBlueprintTemplateIntoBlueprintDetails } from '../../../../infrastructure/utils/blueprint-details.mapper';
import { IBlueprintItemDetails } from '../../../../api/blueprints/blueprint-details.types';
import { BlueprintType } from '../../../../api/blueprints/blueprint.types';
import { IEventBlueprintDTO } from '../../../../api/blueprints/event.blueprint.types';
import { replaceNewLinesWithSpace } from '../../../../infrastructure/utils/string.utils';
import { transpileClassesToInlineStyles } from '../../../global/rich-text-editor/utils/transpiler';
import { TObject } from '../../../../infrastructure/type-extensions';
import { EventAttributeTypesValues, IEventAttributeDetailsDTO } from '../../../../api/attributes.dao';
import UploadImageS3VM from '../../_manage-event/shared/upload-image/upload-image-to-s3.viewmodel';
import { LOCKED_BILLING_STATUSES } from '../../_manage-event/shared/salesforce-order/add-salesforce-order.fields';

const defaultEventData: IEventDetailsDTO = {
    name: '',
    publicDescription: null,
    privateDescription: null,
    defaultMeetingDuration: 0,
    startDateTime: null,
    endDateTime: null,
    eventId: null,
    timeZone: '',
    region: '',
    type: '',
    oeNumber: '',
    orderId: '',
    externalId: '',
    guidelinesLink: '',
    projectPlanLink: '',
    subdomain: '',
    footerLinks: [],
    eventCoordinators: [],
    deliveryTeamMembers: [],
    planningNotes: '',
    clientPlanningNotes: '',
    eventStatus: EventStatus.IN_PROGRESS,
    links: [],
    anonymized: false,
    anonymizationDate: null,
    thirdPartyRegistrationEnabled: false,
    eventRegistrationToken: '',
    blueprintId: null,
    eventTitleForPassport: '',
    registrationApprovalEnabled: false,
    urlParameterRegistrationEnabled: false,
    passportAttendeeSSOEnabled: false,
    attributes: [],
};

type IUserGroupMultiSelectDTO = IUserGroupDTO & { users: IEventCoordinatorDTO[]};

class EventFormDataVM {
    private readonly coordinatorsMapper: CoordinatorItemsMapper;
    private readonly deliveryTeamMembersMapper: DeliveryTeamMemberItemsMapper;

    @mobx.observable
    public name: string;
    @mobx.observable
    public publicDescription: string;
    @mobx.observable
    public privateDescription: string;
    @mobx.observable
    public defaultMeetingDuration: number;
    @mobx.observable
    public startDate: Date;
    @mobx.observable
    public endDate: Date;
    @mobx.observable
    public startDateTime: Date;
    @mobx.observable
    public endDateTime: Date;
    @mobx.observable
    public staffingStartDate: Date | null;
    @mobx.observable
    public staffingEndDate: Date | null;
    @mobx.observable
    public timeZone: string;
    @mobx.observable
    public region: string;
    @mobx.observable
    public businessLine: string;
    @mobx.observable
    public oeNumber: string;
    @mobx.observable
    public orderId: string;
    @mobx.observable
    public externalId: string;
    @mobx.observable
    public guidelinesLink: string;
    @mobx.observable
    public projectPlanLink: string;
    @mobx.observable
    public subdomain: string;
    @mobx.observable
    public footerLinks: FooterLink[];
    @mobx.observable
    private isChecked: boolean;
    @mobx.observable
    private isValidationError: boolean;
    @mobx.observable
    private existingOrderIdError: string;
    @mobx.observable
    private existingExternalIdError: string;
    @mobx.observable
    public type: string;
    @mobx.observable
    public department: string;
    @mobx.observable
    private billingStatus: string | null = null;
    @mobx.observable
    public eventAccess: EventAccess;
    @mobx.observable
    public startTimeValidationError: boolean = false;
    @mobx.observable
    public endTimeValidationError: boolean = false;
    @mobx.observable
    public eventNotes: string;
    @mobx.observable
    public assignedCoordinators: IEventCoordinatorDTO[];
    @mobx.observable
    public selectableCoordinators: IEventCoordinatorDTO[];
    @mobx.observable
    public allCoordinators: IEventCoordinatorDTO[];
    @mobx.observable
    public allUserGroups: IUserGroupMultiSelectDTO[];
    @mobx.observable
    public selectableUserGroups: IUserGroupMultiSelectDTO[];
    @mobx.observable
    public assignedUserGroups: IUserGroupMultiSelectDTO[] = [];
    @mobx.observable
    public assignedDeliveryTeamItems: IDeliveryItemDetailsDTO[];
    @mobx.observable
    public selectableDeliveryTeamItems: IDeliveryItemDetailsDTO[];
    @mobx.observable
    public searchQueryForDeliveryTeam: string = '';
    @mobx.observable
    public planningNotes: string;
    @mobx.observable
    public clientPlanningNotes: string;
    @mobx.observable
    public eventStatus: EventStatus;
    @mobx.observable
    public readonly links: IEventLinkDTO[];
    @mobx.observable
    public thirdPartyRegistrationEnabled: boolean;
    @mobx.observable
    public eventRegistrationToken: string;
    @mobx.observable
    public generatingEventRegistrationToken: boolean = false;
    @mobx.observable
    public blueprintsVM: EventFormBlueprintsVM | null = null;
    @mobx.observable
    public meetingTopic: string = '';
    @mobx.observable
    public eventTitleForPassport: string;
    @mobx.observable
    public showEventTitleForPassport: boolean;
    @mobx.observable
    public registrationApprovalEnabled: boolean;
    @mobx.observable
    public urlParameterRegistrationEnabled: boolean;
    @mobx.observable
    public passportAttendeeSSOEnabled: boolean;
    @mobx.observable
    public eventAttributeValues: IEventAttributeValue[] = [];
    @mobx.observable
    public multiSelectAttributeValues: Map<number, string[]> = new Map();
    @mobx.observable
    private eventAttributes: IEventAttributeDetailsDTO[] = [];
    @mobx.observable
    public uploadThumbnailImageS3VM: UploadImageS3VM = null;

    constructor(
        public readonly accountId: number,
        private readonly apiEvent: Partial<IEventDetailsDTO> = defaultEventData,
        @IoC.Inject private readonly usersDAO: IUsersDAO = IoC.Container.get(IUsersDAO),
        @IoC.Inject private readonly userGroupsDAO: IUserGroupsDAO = IoC.Container.get(IUserGroupsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly eventsDAO: IEventsDAO = IoC.Container.get(IEventsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalViewModel: GlobalViewModel = IoC.Container.get(GlobalViewModel),
    ) {
        this.name = apiEvent.name;
        this.publicDescription = apiEvent.publicDescription;
        this.privateDescription = apiEvent.privateDescription;
        this.planningNotes = apiEvent.planningNotes || '';
        this.clientPlanningNotes = apiEvent.clientPlanningNotes || '';
        // converting time from server to event timezone;
        this.startDate = apiEvent.startDateTime ? utcToZonedTime(apiEvent.startDateTime, apiEvent.timeZone) : apiEvent.startDateTime;
        if (this.startDate) {
            this.startDate.setHours(0, 0, 0);
        }
        this.endDate = apiEvent.endDateTime ? utcToZonedTime(apiEvent.endDateTime, apiEvent.timeZone) : apiEvent.endDateTime;
        if (this.endDate) {
            this.endDate.setHours(0, 0, 0);
        }
        this.startDateTime = apiEvent.startDateTime ? utcToZonedTime(apiEvent.startDateTime, apiEvent.timeZone) : apiEvent.startDateTime;
        this.endDateTime = apiEvent.endDateTime ? utcToZonedTime(apiEvent.endDateTime, apiEvent.timeZone) : apiEvent.endDateTime;
        this.staffingStartDate = apiEvent.staffingStartDate ? new Date(apiEvent.staffingStartDate.getTime()) : null;
        if (this.staffingStartDate) {
            this.staffingStartDate.setHours(0, 0, 0, 0);
        }
        this.staffingEndDate = apiEvent.staffingEndDate ? new Date(apiEvent.staffingEndDate.getTime()) : null;
        if (this.staffingEndDate) {
            this.staffingEndDate.setHours(0, 0, 0, 0);
        }
        this.defaultMeetingDuration = apiEvent.defaultMeetingDuration || 0;
        this.timeZone = apiEvent.timeZone;
        this.region = apiEvent.region || '';
        this.businessLine = apiEvent.businessLine || '';
        this.type = apiEvent.type;
        this.department = apiEvent.department || '';
        this.eventAccess = this.checkEventAccess(apiEvent.eventId, apiEvent.eventAccess);
        this.oeNumber = apiEvent.oeNumber ? apiEvent.oeNumber : '';
        this.orderId = apiEvent.orderId ? apiEvent.orderId : '';
        this.externalId = apiEvent.externalId ? apiEvent.externalId : '';
        this.guidelinesLink = apiEvent.guidelinesLink ? apiEvent.guidelinesLink : '';
        this.projectPlanLink = apiEvent.projectPlanLink ? apiEvent.projectPlanLink : '';
        this.subdomain = apiEvent.subdomain;
        this.footerLinks = apiEvent.footerLinks;
        this.existingOrderIdError = null;
        this.existingExternalIdError = null;
        this.eventNotes = apiEvent.eventNotes;
        this.assignedCoordinators = apiEvent?.eventCoordinators ?? defaultEventData.eventCoordinators;
        this.assignedDeliveryTeamItems = apiEvent.deliveryTeamMembers ?? defaultEventData.deliveryTeamMembers;
        this.eventStatus = apiEvent.eventStatus ?? defaultEventData.eventStatus;
        this.links = apiEvent.links ?? defaultEventData.links;

        this.coordinatorsMapper = CoordinatorItemsMapper.create(apiEvent.eventCoordinators);
        this.deliveryTeamMembersMapper = DeliveryTeamMemberItemsMapper.create(apiEvent.deliveryTeamMembers);
        this.thirdPartyRegistrationEnabled = apiEvent.thirdPartyRegistrationEnabled ?? true;
        this.eventRegistrationToken = apiEvent.eventRegistrationToken ?? '';
        this.registrationApprovalEnabled = apiEvent.registrationApprovalEnabled ?? true;
        this.urlParameterRegistrationEnabled = apiEvent.urlParameterRegistrationEnabled ?? defaultEventData.urlParameterRegistrationEnabled;
        this.passportAttendeeSSOEnabled = apiEvent.passportAttendeeSSOEnabled ?? defaultEventData.passportAttendeeSSOEnabled;

        if (this.hasBlueprintsPermissions()) this.initBlueprintsVM();

        this.eventTitleForPassport = apiEvent.eventTitleForPassport ?? '';
        this.showEventTitleForPassport = !!apiEvent.eventTitleForPassport;

        this.initThumbnailImageVM();

        if (apiEvent.attributes) {
            const singleValueAttributes: IEventAttributeValue[] = [];
            apiEvent.attributes.forEach(attr => {
                if (attr.multiAttributeValues && attr.multiAttributeValues.length > 0) {
                    this.multiSelectAttributeValues.set(attr.attributeId, attr.multiAttributeValues);
                } else if (attr.attributeValue !== undefined && attr.attributeValue !== null) {
                    singleValueAttributes.push(attr);
                }
            });
            this.eventAttributeValues = singleValueAttributes;
        }
    }

    public get blueprintsAvailable(): boolean {
        return !!this.blueprintsVM;
    }

    public get isEditEvent(): boolean {
        return !!this.apiEvent.eventId;
    }

    public getBlueprintsOptions(): {blueprintId: string; name: string}[] {
        return this.blueprintsVM.getBlueprintList()
            .map(({ blueprintId, name }) => ({ blueprintId: blueprintId, name: name }))
        ;
    }

    public getSelectedBlueprintName(): string {
        return this.blueprintsVM.getBlueprintName();
    }

    public getSelectedBlueprint(): IEventBlueprintDTO {
        return this.blueprintsVM.getSelectedBlueprint();
    }

    public getSelectedBlueprintType(): BlueprintType {
        return this.getSelectedBlueprint() && this.getSelectedBlueprint().type;
    }

    public getSelectedBlueprintDetails(): IBlueprintItemDetails | null {
       const selectedBlueprint = this.getSelectedBlueprint();

        return selectedBlueprint
            ? convertBlueprintTemplateIntoBlueprintDetails(
                selectedBlueprint.type,
                selectedBlueprint,
                this.globalViewModel.getTimezonesVM().getTimezonesList() || [],
                this.blueprintsVM.getMeetingTemplateList()
            )
            : null;
    }

    public get isLoadingBlueprints(): boolean {
        return this.blueprintsVM.isLoading;
    }

    public getSelectedBlueprintId(): string {
        return this.blueprintsVM.blueprintId || '';
    }

    public blueprintWithEventConfiguration(): boolean {
        return this.blueprintsAvailable && this.blueprintsVM.blueprintHasPassportSiteAction();
    }

    public blueprintWithSingleMeetingConfiguration(): boolean {
        return this.blueprintsAvailable && this.blueprintsVM.blueprintHasSingleMeetingAction();
    }

    public showMeetingTopicField(): boolean {
        if (this.isEditEvent) {
            return this.blueprintWithSingleMeetingConfiguration() && this.isBlueprintIdChanged();
        } else {
            return this.blueprintWithSingleMeetingConfiguration();
        }
    }

    public isBlueprintIdChanged(): boolean {
        return this.blueprintsAvailable && this.apiEvent.blueprintId !== this.blueprintsVM.blueprintId;
    }

    @mobx.action.bound
    public selectBlueprint(blueprintId: string): void {
        this.blueprintsVM.setBlueprintId(blueprintId);

        if (blueprintId) {
            this.applyBlueprint();
        } else {
            this.resetMeetingTopic();
        }
    }

    @mobx.action.bound
    public setMeetingTopic(value: string): void {
        this.meetingTopic = value;
    }

    @mobx.action.bound
    public resetMeetingTopic(): void {
        this.meetingTopic = '';
    }

    public toApiDTO(): Omit<ICreateEventDTO, 'coordinators'> {
        const dto: Omit<ICreateEventDTO, 'coordinators'> = {
            name: this.name.replace(/ +/g, ' ').trim(),
            // Since CKEditor works with classes, but data is stored with inline styles,
            // all CKEditor values need to be converted to the appropriate format
            publicDescription: transpileClassesToInlineStyles(this.publicDescription),
            privateDescription: transpileClassesToInlineStyles(this.privateDescription),
            planningNotes: transpileClassesToInlineStyles(this.planningNotes || ''),
            clientPlanningNotes: transpileClassesToInlineStyles(this.clientPlanningNotes || ''),
            // sending to server time in UTC;
            startDateTime: zonedTimeToUtc(this.startDateTime, this.timeZone),
            endDateTime: zonedTimeToUtc(this.endDateTime, this.timeZone),
            staffingStartDate: this.staffingStartDate || null,
            staffingEndDate: this.staffingEndDate || null,
            defaultMeetingDuration: this.defaultMeetingDuration ? this.defaultMeetingDuration : null,
            timeZone: this.timeZone,
            region: this.region || null,
            businessLine: this.businessLine || null,
            type: this.type,
            eventAccess: this.eventAccess,
            oeNumber: this.toSalesforceLinkField(this.oeNumber, this.apiEvent.oeNumber),
            orderId: this.toSalesforceLinkField(this.orderId, this.apiEvent.orderId),
            externalId: this.externalId ? this.externalId.trim() : null,
            guidelinesLink: this.guidelinesLink.trim(),
            projectPlanLink: this.projectPlanLink.trim(),
            subdomain: this.subdomain,
            footerLinks: this.footerLinks,
            eventNotes: this.eventNotes,
            eventStatus: this.eventStatus,
            blueprintId: this.blueprintsVM ? this.blueprintsVM.blueprintId : this.apiEvent.blueprintId,
            addedCoordinators: this.getAddedCoordinators(),
            removedCoordinators: this.getRemovedCoordinators(),
            addedDeliveryTeamMembers: this.getAddedDeliveryTeamMembers(),
            removedDeliveryTeamMembers: this.getRemovedDeliveryTeamMembers(),
            updatedDeliveryTeamMembers: this.getUpdatedDeliveryTeamMembers(),
            department: this.department || null,
            attributes: this.getEventAttributeValuesForApi(),
        };

        const trimmedMeetingTopic = this.meetingTopic.trim();
        if (trimmedMeetingTopic) dto.blueprintInputs = { topic: trimmedMeetingTopic };

        if (this.isEditEvent) {
            dto.eventTitleForPassport = this.showEventTitleForPassport ? this.eventTitleForPassport : '';
            const thumbnailImage = this.uploadThumbnailImageS3VM.getImageToApi();
            return { ...dto, ...thumbnailImage };
        }

        return dto;
    }

    /**
     * Explicit unlink is an empty string; omitted/null means "do not change" so a concurrent Salesforce
     * link is not wiped when the form opened without an Order ID / OE Number.
     */
    private toSalesforceLinkField(current: string, original: string | null | undefined): string | null {
        const trimmed = (current || '').trim();
        if (trimmed) {
            return trimmed;
        }
        return original ? '' : null;
    }

    public isImageUploading(): boolean {
        return this.uploadThumbnailImageS3VM.isImageUploading;
    }

    public getExistingOrderIdError() {
        return this.existingOrderIdError;
    }

    public getExistingExternalIdError() {
        return this.existingExternalIdError;
    }

    @mobx.action
    public onChangeDefaultDuration(event) {
        this.defaultMeetingDuration = event.currentTarget.value |= 0;
    }

    @mobx.action
    public setExistingOrderIdError(errorMsg: string) {
        this.existingOrderIdError = errorMsg;
    }

    @mobx.action
    public setExistingExternalIdError(errorMsg: string) {
        this.existingExternalIdError = errorMsg;
    }

    @mobx.action
    public checkFormValidity() {
        this.isChecked = true;

        return !this.getEndTimeError() &&
            !this.getStartTimeError() &&
            !this.getNameRequiredError() &&
            !getUrlError('Guidelines Link', this.guidelinesLink) &&
            !getUrlError('Project Plan Link', this.projectPlanLink) &&
            !this.getDefaultMeetingDurationError() &&
            !this.getExistingOrderIdError() &&
            !this.getExistingExternalIdError() &&
            !this.getTimeZoneError() &&
            !this.getTypeError();
    }

    @mobx.action
    public setEventAttributes(attributes: IEventAttributeDetailsDTO[]): void {
        this.eventAttributes = attributes;
    }

    @mobx.action
    public checkFormDynamicValidity() {
        this.isValidationError = true;

        return !getShortTextLengthError('Event Name', this.name) &&
            !getShortTextLengthError('OE Number', this.oeNumber) &&
            !getShortTextLengthError('Order ID', this.orderId) &&
            !getShortTextLengthError('External ID', this.externalId) &&
            !this.getDatesError() &&
            !this.getTimesError() &&
            !this.getStaffingDatesError() &&
            !this.getPublicDescriptionError() &&
            !this.getPrivateDescriptionError() &&
            !this.getPlanningNotesError() &&
            !this.getClientPlanningNotesError() &&
            !this.getMeetingTopicErrorMessage() &&
            !this.getEventTitleForPassportError() &&
            !this.hasFreeTextAttributeErrors(this.eventAttributes) &&
            !this.hasParagraphAttributeErrors(this.eventAttributes);
    }

    public getTimeZone() {
        return this.timeZone;
    }

    public getRegion() {
        return this.region;
    }

    public getType() {
        return this.type;
    }

    public getClientDepartment(): string {
        return this.department;
    }

    @mobx.action
    public setBillingStatus(billingStatus: string | null): void {
        this.billingStatus = billingStatus;
    }

    public get isClientDepartmentLocked(): boolean {
        if (hasPermission(Permission.OVERRIDE_BILLING_LOCK)) {
            return false;
        }
        return this.billingStatus != null && LOCKED_BILLING_STATUSES.has(this.billingStatus);
    }

    public getDepartmentsOptions(): TObject<ClientDepartmentValues>[] {
        return Object.keys(ClientDepartmentValues).map((key: ClientDepartmentValues) => {
            return { label: ClientDepartmentValues[key], value: key };
        });
    }

    public getEventAccess() {
        return this.eventAccess;
    }

    public getStartDate() {
        return this.startDate;
    }

    public getEndDate() {
        return this.endDate;
    }

    public getStaffingStartDate() {
        return this.staffingStartDate;
    }

    public getStaffingEndDate() {
        return this.staffingEndDate;
    }

    public showErrors() {
        return this.isChecked;
    }

    public isValidationErrorExist() {
        return !this.checkFormDynamicValidity() || this.showErrors() && !this.checkFormValidity();
    }

    public showValidateErrors() {
        return this.isValidationError;
    }

    public getDatesError(): JSX.Element | null {
        return this.startDate && this.endDate && !(isAfter(this.endDate, this.startDate) || isEqual(this.endDate, this.startDate))
            ? <FormattedMessage
                description='Warning message'
                defaultMessage='Event end date should not be before the start date'
                id='oe.eventAndDate.shouldNotBeforeStart'
            /> : null;
    }

    public getStaffingDatesError(): JSX.Element | null {
        if (this.staffingStartDate && this.startDate && isAfter(this.staffingStartDate, this.startDate)) {
            return <FormattedMessage
                description='Warning message'
                defaultMessage='Staffing Start Date should not be after the Start Date'
                id='oe.eventAndDate.staffingStartAfterStart'
            />;
        }
        if (this.staffingEndDate && this.endDate && isBefore(this.staffingEndDate, this.endDate)) {
            return <FormattedMessage
                description='Warning message'
                defaultMessage='Staffing End Date should not be before the End Date'
                id='oe.eventAndDate.staffingEndBeforeEnd'
            />;
        }
        return null;
    }

    public getTimesError(): JSX.Element | null {
        if (this.startTimeValidationError && this.endTimeValidationError) {
            return <FormattedMessage
                id='oe.meeting.invalidStartTimeOrEndTime'
                defaultMessage='Invalid Start Time or End Time'
                description='Validation error message'
            />;
        }
        if (this.startTimeValidationError) {
            return <FormattedMessage
                id='oe.meeting.invalidStartTime'
                defaultMessage='Invalid Start Time'
                description='Validation error message'
            />;
        }
        if (this.endTimeValidationError) {
            return <FormattedMessage
                id='oe.meeting.invalidEndTime'
                defaultMessage='Invalid End Time'
                description='Validation error message'
            />;
        }
        if (this.startDateTime && this.endDateTime && !isBefore(this.startDateTime, this.endDateTime)) {
            return <FormattedMessage
                description='Warning message'
                defaultMessage='Event end time should not be before the start time'
                id='oe.eventAndTime.shouldNotBeforeStart'
            />;
        }
        return null;
    }

    public getStartDateError(): JSX.Element | null {
        return !this.startDate ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'Start date'
            }}
        /> : null;
    }

    public getEndDateError(): JSX.Element | null {
        return !this.endDate ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'End date'
            }}
        /> : null;
    }

    public getStartTimeError(): JSX.Element | null {
        return !this.startDateTime ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'Start time'
            }}
        /> : null;
    }

    public getEndTimeError(): JSX.Element | null {
        return !this.endDateTime ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'End time'
            }}
        /> : null;
    }

    public getDefaultMeetingDurationError(): JSX.Element | null {
        if (this.defaultMeetingDuration < 0 || this.defaultMeetingDuration > 1440 || Number.isNaN(this.defaultMeetingDuration)) {
            return <FormattedMessage
                description='Warning message'
                defaultMessage='Field value should be between 0 and 1440'
                id='oe.common.durationFieldIsNotCorrect'
            />;
        }
        else return null;
    }

    public getNameRequiredError(): JSX.Element | null {
        return !this.name ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'Event Name'
            }}
        /> : null;
    }

    public getNameErrorMessage(): JSX.Element | string {
        return getShortTextLengthError('Event Name', this.name) || this.showErrors() && this.getNameRequiredError();
    }

    public getPublicDescriptionError(): JSX.Element | null {
        return calcByteCodeSize(this.publicDescription) > LONG_RICH_TEXT_LENGTH ?
            <FormattedMessage
                description='Warning message'
                defaultMessage='{field} is too long'
                id='oe.common.fieldIsTooLong'
                values={{
                    field: 'Public description'
                }}
            /> : null;
    }

    public getPrivateDescriptionError(): JSX.Element | null {
        return calcByteCodeSize(this.privateDescription) > LONG_RICH_TEXT_LENGTH ?
            <FormattedMessage
                description='Warning message'
                defaultMessage='{field} is too long'
                id='oe.common.fieldIsTooLong'
                values={{
                    field: 'Private description'
                }}
            /> : null;
    }

    public getPlanningNotesError(): JSX.Element | null {
        return calcByteCodeSize(this.planningNotes) > LONG_RICH_TEXT_LENGTH ?
            <FormattedMessage
                description='Warning message'
                defaultMessage='{field} is too long'
                id='oe.common.fieldIsTooLong'
                values={{
                    field: 'Planning Notes'
                }}
            /> : null;
    }

    public getClientPlanningNotesError(): JSX.Element | null {
        return calcByteCodeSize(this.clientPlanningNotes) > LONG_RICH_TEXT_LENGTH ?
            <FormattedMessage
                description='Warning message'
                defaultMessage='{field} is too long'
                id='oe.common.fieldIsTooLong'
                values={{
                    field: 'Client Planning Notes'
                }}
            /> : null;
    }

    public getTimeZoneError(): JSX.Element | null {
        return !this.timeZone ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'Timezone'
            }}
        /> : null;
    }

    public getTypeError(): JSX.Element | null {
        return !this.type ? <FormattedMessage
            description='Warning message'
            defaultMessage='{field} is required'
            id='oe.common.fieldIsRequired'
            values={{
                field: 'Event Type'
            }}
        /> : null;
    }


    public getOeNumberErrorMessage() {
        return getShortTextLengthError('OE Number', this.oeNumber) || this.showErrors();
    }

    public getOrderIdErrorMessage() {
        return getShortTextLengthError('Order ID', this.orderId) || this.getExistingOrderIdError() || this.showErrors();
    }

    public getExternalIdErrorMessage() {
        return getShortTextLengthError('External ID', this.externalId) || this.getExistingExternalIdError() || this.showErrors();
    }

    public getGuidelinesLinkErrorMessage() {
        return getUrlError('Guidelines Link', this.guidelinesLink) || this.showErrors();
    }

    public getProjectPlanLinkErrorMessage() {
        return getUrlError('Project Plan Link', this.projectPlanLink) || this.showErrors();
    }

    public getDefaultMeetingDurationErrorMessage() {
        return this.getDefaultMeetingDurationError() || this.showErrors();
    }

    public getMeetingTopicErrorMessage(): string | null {
        return getShortTextLengthError('Meeting Topic', this.meetingTopic) || null;
    }

    @mobx.action
    public refreshOrderIdError = () => {
        mobx.runInAction(() => {
            this.existingOrderIdError = null;
        });
    };

    @mobx.action
    public refreshExternalIdError = () => {
        mobx.runInAction(() => {
            this.existingExternalIdError = null;
        });
    };

    @mobx.action
    public setStartDate = (date: Date) => {
        this.startDate = date;
        if (this.startDateTime) {
            this.startDateTime.setFullYear(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate());
        }
        if (this.staffingStartDate && date && isBefore(date, this.staffingStartDate)) {
            this.setStaffingStartDate(date);
        }
    };

    @mobx.action
    public setEndDate = (date: Date) => {
        this.endDate = date;
        if (this.endDateTime) {
            this.endDateTime.setFullYear(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate());
        }
        if (this.staffingEndDate && date && isAfter(date, this.staffingEndDate)) {
            this.setStaffingEndDate(date);
        }
    };

    @mobx.action
    public setStaffingStartDate = (date: Date | null) => {
        this.staffingStartDate = date ? startOfDay(date) : null;
    };

    @mobx.action
    public setStaffingEndDate = (date: Date | null) => {
        this.staffingEndDate = date ? startOfDay(date) : null;
    };

    @mobx.action
    public setStartTime = (date: Date) => {
        this.startTimeValidationError = false;
        this.startDateTime = new Date(this.startDate.getTime());
        this.startDateTime.setHours(date.getHours(), date.getMinutes());
    };

    @mobx.action
    public setEndTime = (date: Date) => {
        this.endTimeValidationError = false;
        this.endDateTime = new Date(this.endDate.getTime());
        this.endDateTime.setHours(date.getHours(), date.getMinutes());
    };

    @mobx.action
    public removeCoordinator = (username: string) => {
        this.assignedCoordinators = this.assignedCoordinators.filter(coordinator => coordinator.username !== username);
        this.updateAssignedUserGroups();
    };

    @mobx.action
    public searchCoordinators = (query: string): void => {
        if (this.allCoordinators?.length) {
            if (!query || query.trim().length >= MINIMAL_SEARCH_LENGTH) {
                this.selectableCoordinators = this.allCoordinators.filter(({name}) => {
                        return name.toLocaleLowerCase().includes(query?.toLocaleLowerCase() || '');
                    }
                );
            }
        }
    };

    @mobx.action
    public setAssignedCoordinators = (coordinators: IEventCoordinatorDTO[]): void => {
        this.assignedCoordinators = coordinators;
        this.updateAssignedUserGroups();
    };

    @mobx.action
    public setEventAccess = (access: EventAccess): void => {
        this.eventAccess = access;
    };

    @mobx.action
    public setEventStatus = (status: EventStatus): void => {
        this.eventStatus = status;
    };

    public fetchCoordinatorsList = async (): Promise<void> => {
        const response = await this.usersDAO.getAccountUsers({ accountId: this.accountId, activeOnly: true});
        const formattedResponse = response.map(coordinator => ({...coordinator, name: `${coordinator.firstName} ${coordinator.lastName}`}));
        mobx.runInAction(() => {
            this.allCoordinators = formattedResponse;
            this.selectableCoordinators = formattedResponse;
        });
    };


    @mobx.action
    public assignCoordinatorsFromGroupsList = (groups: IUserGroupDTO[]) => {
        const tempAssignedCoordinatorsList = this.assignedCoordinators;
        const tempCoordinatorsFromGroups = [].concat(...groups.map(group => group.users));
        tempAssignedCoordinatorsList.push(...tempCoordinatorsFromGroups);

        const uniqueCoordinatorsList = this.getUniqueArrayById(tempAssignedCoordinatorsList, 'username') as IEventCoordinatorDTO[];
        this.assignedCoordinators = uniqueCoordinatorsList;
        this.updateAssignedUserGroups();
    };


    public fetchUserGroupsList = async (): Promise<void> => {
        const response = await this.userGroupsDAO.getUserGroups({
            page: 0,
            size: 10000, // change FE filtering into server search after adding query for user groups (reduce size to 100)
            accountId: this.accountId
        });
        const formattedResponse = response.content.map(group => ({
            ...group,
            users: group.users.map(user => ({...user, name: `${user.firstName} ${user.lastName}`})),
            hoverLabel: this.getGroupUsersLabel(group)
        }));

        mobx.runInAction(() => {
            this.allUserGroups = formattedResponse;
            this.selectableUserGroups = formattedResponse;
            this.updateAssignedUserGroups();
        });
    };

    public fetchDeliveryTeamItems = async (): Promise<void> => {
        try {
            const response = await this.usersDAO.searchDeliveryTeamMembers({
                page: 0,
                size: 100,
                query: this.searchQueryForDeliveryTeam ? this.searchQueryForDeliveryTeam.trim() : '',
                activeOnly: true
            });
            const formattedResponse = response.users.map(deliveryItem => (
                {...deliveryItem, fullName: `${deliveryItem.firstName} ${deliveryItem.lastName}`}
            ));
            mobx.runInAction(() => {
                this.selectableDeliveryTeamItems = formattedResponse;
            });
        } catch (error) {
            if (error.response.status !== 403) {
                this.onFetchUsersListError(error);
            }
        }
    };

    @mobx.action
    public setAssignedDeliveryTeam = (deliveryTeamItems: IDeliveryItemDetailsDTO[]) => {
        this.assignedDeliveryTeamItems = deliveryTeamItems;
    };

    @mobx.action
    public removeDeliveryTeamItem = (username: string) => {
        this.assignedDeliveryTeamItems = this.assignedDeliveryTeamItems.filter(item => item.username !== username);
    };

    @mobx.action
    public searchDeliveryTeamItems = async (query: string): Promise<void> => {
        this.searchQueryForDeliveryTeam = query;
        await this.fetchDeliveryTeamItems();
    };

    @mobx.action
    public setDeliveryMemberRole(username: string, role: string): void {
        const currentMemberIndex = this.assignedDeliveryTeamItems.findIndex(item => item.username === username);
        this.assignedDeliveryTeamItems[currentMemberIndex].deliveryTeamMemberRole =
            role === this.assignedDeliveryTeamItems[currentMemberIndex].deliveryTeamMemberRole ? null : role;
    }

    @mobx.action
    public createEventRegistrationToken = async (): Promise<void> => {
        try {
            mobx.runInAction(() => {
                this.generatingEventRegistrationToken = true;
            });
            const response = await this.eventsDAO.createEventRegistrationToken({
                accountId: this.accountId,
                eventId: this.apiEvent.eventId,
            });
            mobx.runInAction(() => {
                this.eventRegistrationToken = response.eventRegistrationToken;
            });
            this.trackingService.get().track(TRACKING_EVENTS.GENERATE_EVENT_REGISTRATION_TOKEN, {
                'Event ID': this.apiEvent.eventId,
                'Event Name': this.apiEvent.name,
                'Account ID': this.apiEvent.accountId,
                'Account Name': this.apiEvent.accountName
            });
        } catch (error) {
            alertServerError('Error', this.errorParsingService.extractMessage(error));
        } finally {
            mobx.runInAction(() => {
                this.generatingEventRegistrationToken = false;
            });
        }
    };

    @mobx.action.bound
    public toggleCustomMeetingTitleForPassport(): void {
        this.showEventTitleForPassport = !this.showEventTitleForPassport;
    }
    @mobx.action
    public setEventTitleForPassport(value: string): void {
        this.eventTitleForPassport = value;
    }

    public getEventTitleForPassportError(): string | null {
        return getShortTextLengthError('Event Title for Passport', this.eventTitleForPassport) || null;
    }

    public getAttributeValue(eventAttributeId: number, asBoolean: boolean = false): string | boolean | null {
        const attrValue = this.eventAttributeValues.find(attribute => attribute.attributeId === eventAttributeId);
        if ( asBoolean ) {
            return attrValue?.attributeValue === 'true' ? true : false;
        }
        return attrValue?.attributeValue || null;
    }

    public getMultiSelectAttributeValues(eventAttributeId: number): string[] {
        return this.multiSelectAttributeValues.get(eventAttributeId) || [];
    }

    @mobx.action
    public setMultiSelectAttributeValues(eventAttributeId: number, values: string[]): void {
        if (values.length > 0) {
            this.multiSelectAttributeValues.set(eventAttributeId, values);
        } else {
            this.multiSelectAttributeValues.delete(eventAttributeId);
        }
    }

    @mobx.action
    public removeMultiSelectAttributeValue(eventAttributeId: number, value: string): void {
        const currentValues = this.multiSelectAttributeValues.get(eventAttributeId) || [];
        const updatedValues = currentValues.filter(v => v !== value);
        if (updatedValues.length > 0) {
            this.multiSelectAttributeValues.set(eventAttributeId, updatedValues);
        } else {
            this.multiSelectAttributeValues.delete(eventAttributeId);
        }
    }

    public getSelectedAttributeValues = (attribute: IEventAttributeDetailsDTO):{ valueId: string; label: string }[] => {
        const activeValues = attribute.values.filter(v => v.active);
        const selectedValueIds = this.getMultiSelectAttributeValues(attribute.eventAttributeId);
        const selectedItems = selectedValueIds.map(vId => {
            const value = activeValues.find(val => String(val.valueId) === vId);
            return value ? { valueId: vId, label: value.label } : null;
        }).filter(Boolean);
        return selectedItems;
    };

    @mobx.action
    public setAttributeValue(eventAttributeId: number, value: string | boolean): void {
        const newAttribute = { attributeId: eventAttributeId, attributeValue: String(value) };
        const attributeIndex = this.eventAttributeValues.findIndex(attribute => attribute.attributeId === eventAttributeId );
        if (attributeIndex !== -1) {
            this.eventAttributeValues[attributeIndex] = newAttribute;
        } else {
            this.eventAttributeValues.push(newAttribute);
        }
    }

    public getFreeTextAttributeError(eventAttributeId: number): JSX.Element | null {
        const value = this.getAttributeValue(eventAttributeId) as string || '';
        return value?.length > SHORT_PLAIN_TEXT_LENGTH ?
            <FormattedMessage {...SHORT_PLAIN_TEXT_TOO_LONG_ERROR_TEXT} /> : null;
    }

    public hasFreeTextAttributeErrors(freeTextAttributes: IEventAttributeDetailsDTO[]): boolean {
        return freeTextAttributes.some(attr =>
            attr.inputType === EventAttributeTypesValues.FREE_TEXT &&
            this.getFreeTextAttributeError(attr.eventAttributeId)
        );
    }

    public getParagraphAttributeError(eventAttributeId: number): JSX.Element | null {
        const value = this.getAttributeValue(eventAttributeId) as string || '';
        return value?.length > LONG_PLAIN_TEXT_LENGTH ?
            <FormattedMessage {...LONG_PLAIN_TEXT_TOO_LONG_ERROR_TEXT} /> : null;
    }

    public hasParagraphAttributeErrors(attributes: IEventAttributeDetailsDTO[]): boolean {
        return attributes.some(attr =>
            attr.inputType === EventAttributeTypesValues.PARAGRAPH &&
            this.getParagraphAttributeError(attr.eventAttributeId)
        );
    }

    private getEventAttributeValuesForApi(): IEventAttributeValue[] {
        const result: IEventAttributeValue[] = [];
        this.eventAttributeValues.forEach(attr => {
            if (attr.attributeValue !== null && attr.attributeValue !== undefined) {
                result.push({ attributeId: attr.attributeId, attributeValue: attr.attributeValue });
            }
        });
        this.multiSelectAttributeValues.forEach((values, attributeId) => {
            if (values.length > 0) {
                result.push({ attributeId: attributeId, multiAttributeValues: values });
            }
        });
        return result;
    }

    private getFormattedEventTitleForPassport(): string {
        return replaceNewLinesWithSpace(this.eventTitleForPassport).trim();
    }

    @mobx.action
    private applyBlueprint(): void {
        const fieldsValues = this.blueprintsVM.getBlueprintFieldsValues();

        if (fieldsValues.timeZone) this.timeZone = fieldsValues.timeZone as string;
        if (fieldsValues.region) this.region = fieldsValues.region as string;
        if (fieldsValues.businessLine) this.businessLine = fieldsValues.businessLine as string;
        if (fieldsValues.type) this.type = fieldsValues.type as string;
        if (fieldsValues.eventAccess) this.eventAccess = fieldsValues.eventAccess as EventAccess;
        if (fieldsValues.defaultMeetingDuration) this.defaultMeetingDuration = fieldsValues.defaultMeetingDuration as number;

        if (this.blueprintWithSingleMeetingConfiguration() && this.isBlueprintIdChanged()) {
            const meetingTopic = this.showEventTitleForPassport && this.getFormattedEventTitleForPassport() || this.name;
            this.setMeetingTopic(meetingTopic);
        } else {
            this.resetMeetingTopic();
        }
    }

    private hasBlueprintsPermissions(): boolean {
        if (this.isEditEvent) {
            return hasPermission(Permission.BLUEPRINTS_TAB) && hasPermission(Permission.EDIT_EVENT_FROM_BLUEPRINT);
        } else {
            return hasPermission(Permission.BLUEPRINTS_TAB) && hasPermission(Permission.CREATE_EVENT_FROM_BLUEPRINT);
        }
    }

    private initBlueprintsVM(): void {
        this.blueprintsVM = new EventFormBlueprintsVM(this.apiEvent.blueprintId, this.accountId);
        this.blueprintsVM.initialize();
    }

    private initThumbnailImageVM(): void {
        this.uploadThumbnailImageS3VM = new UploadImageS3VM(this.accountId, this.apiEvent.eventId, 'thumbnailImage', 'event');
        this.uploadThumbnailImageS3VM.addImage(
            this.apiEvent.thumbnailImageWithUrl?.assetUrl,
            this.apiEvent.thumbnailImage?.title,
            this.apiEvent.thumbnailImage?.objectKey
        );
    }

    private getAddedCoordinators(): IEventCoordinatorDTO[] {
        return this.coordinatorsMapper.getAddedCoordinatorsToList(this.assignedCoordinators);
    }
    private getRemovedCoordinators(): IEventCoordinatorDTO[] {
        return this.coordinatorsMapper.getRemovedCoordinatorsFromList(this.assignedCoordinators);
    }

    private getAddedDeliveryTeamMembers(): IDeliveryItemDetailsDTO[] {
        return this.deliveryTeamMembersMapper.getAddedDeliveryTeamMembersToList(this.assignedDeliveryTeamItems);
    }
    private getRemovedDeliveryTeamMembers(): IDeliveryItemDetailsDTO[] {
        return this.deliveryTeamMembersMapper.getRemovedDeliveryTeamMembersFromList(this.assignedDeliveryTeamItems);
    }
    private getUpdatedDeliveryTeamMembers(): IDeliveryItemDetailsDTO[] {
        return this.deliveryTeamMembersMapper.getUpdatedDeliveryTeamMembersInList(this.assignedDeliveryTeamItems);
    }

    @mobx.action
    private onFetchUsersListError(error): void {
        this.alertsService.alert({
            message: this.errorParsingService.extractMessage(error)
        });
    }

    @mobx.action
    private updateAssignedUserGroups = (): void => {
        if (this.allUserGroups) {
            this.assignedUserGroups = this.allUserGroups.filter(group => {
                return group.users.map(coordinator => coordinator.username)
                .every(v => this.assignedCoordinators.map(user => user.username).includes(v));
            });
        }
    };

    private getGroupUsersLabel = (group: IUserGroupDTO): string => {
        let usersListLabel = stringifyStaffMembers(group.users, TOOLTIP_GROUP_USERS_COUNT).join(',\n');
        if (group.users.length > TOOLTIP_GROUP_USERS_COUNT) usersListLabel += `\n\n+${group.users.length - TOOLTIP_GROUP_USERS_COUNT} others`;

        return usersListLabel;
    };

    private getUniqueArrayById = (array: object[], id: string): object[] => {
        const uniqueList = [];
        const map = new Map();
        array.forEach(item => {
            if (!map.has(item[id])) {
                map.set(item[id], true);
                uniqueList.push(item);
            }
        });

        return uniqueList;
    };

    private checkEventAccess(eventId: number | null, eventAccess: EventAccess | null): EventAccess {
        if (eventId && !eventAccess) return EventAccess.Open;
        return eventAccess || EventAccess.Restricted;
    }
}

export { EventFormDataVM };
